import React, { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import CodeHelp from './CodeHelp';
import TurnstileWidget, { turnstileEnabled } from './TurnstileWidget';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Email, then code. Superadmins get a third step (password) since the code
// alone isn't enough for that account type — see EmailCodeSuperadminPassword
// in auth.py. Keep the single code field and resend cooldown; presentation
// changes must not bypass the password step or disclose account existence.
const EmailCodeLoginForm = ({ onLoginSuccess, onCreateAccount, initialEmail = '' }) => {
  const [step, setStep] = useState('email'); // 'email' | 'code' | 'password'
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [stepUpToken, setStepUpToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // Mirrors the server's 60s resend window so the wait is shown, not guessed at.
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  // Cloudflare Turnstile token: single-use, so reset after every request. Held
  // in a ref, not state, because handleRequestCode may have to wait for a fresh
  // one mid-call and a state value captured at render would be stale by then.
  const botTokenRef = useRef('');
  const turnstileRef = useRef(null);
  const rememberToken = (token) => { botTokenRef.current = token || ''; };
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [cooldownUntil]);

  // Every request resets the widget, so the next starts with no token.
  // Turnstile re-issues within a second or two in interaction-only mode. The
  // old code failed instantly and told people to tick a checkbox rendered
  // below a tall help panel -- off-screen on a phone.
  const waitForToken = async () => {
    for (let attempt = 0; attempt < 25; attempt += 1) {
      if (botTokenRef.current) return botTokenRef.current;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => { setTimeout(resolve, 200); });
    }
    return '';
  };

  const requestEmailCode = useAuthStore((state) => state.requestEmailCode);
  const loginWithEmailCode = useAuthStore((state) => state.loginWithEmailCode);
  const verifySuperadminPassword = useAuthStore((state) => state.verifySuperadminPassword);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    // Our own message, under the field, instead of the browser's bubble that
    // vanishes on scroll and follows the browser's language.
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError('Enter an email address like you@company.com.');
      return;
    }
    setError('');
    setIsLoading(true);
    try {
      let token = botTokenRef.current;
      // Still no token (a blocked or broken widget): send anyway and let the
      // API decide. It requires one only once its secret is set, so the widget
      // can never block sign-in on its own.
      if (turnstileEnabled() && !token) token = await waitForToken();
      if (token) await requestEmailCode(email, token);
      else await requestEmailCode(email);
      // Every address now gets an email: the code, or a note that there is no
      // account yet. The words are the same either way, so they reveal nothing.
      // The timestamp makes a resend visibly different from the first send --
      // the API answers 200 either way, including when it suppresses a resend
      // inside its own window, so without this the button looks dead.
      const at = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      setInfo(`${step === 'code' ? 'Email sent again' : 'Email sent'} at ${at}.`);
      setCooldownUntil(Date.now() + 60000);
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not send code. Please try again.');
    } finally {
      setIsLoading(false);
      turnstileRef.current?.reset();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await loginWithEmailCode(email, code);
      if (result?.requires_password) {
        // Superadmin: the code is only the first factor. Move to the
        // password step instead of finishing sign-in.
        setStepUpToken(result.step_up_token);
        setInfo('');
        setStep('password');
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
      onLoginSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid or expired code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await verifySuperadminPassword(email, stepUpToken, password);
      await new Promise((resolve) => setTimeout(resolve, 50));
      onLoginSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Invalid credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-7">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-950">
          {step === 'email' ? 'Welcome back' : step === 'code' ? 'Check your email' : 'Confirm it’s you'}
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          {step === 'email' && "We'll email you a single-use code — no password needed."}
          {step === 'code' && <>If <strong className="break-words font-medium text-gray-900">{email.trim()}</strong> has a Serviti account, enter its 6-digit code below.</>}
          {step === 'password' && 'Enter your password to finish signing in.'}
        </p>
      </div>

      {step === 'email' && (
        <form className="space-y-5" onSubmit={handleRequestCode} noValidate>
          <div>
            <label htmlFor="email-code-email" className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              id="email-code-email"
              type="email"
              name="email"
              required
              autoComplete="email"
              aria-invalid={Boolean(error) || undefined}
              aria-describedby={error ? 'email-code-email-error' : undefined}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 appearance-none relative block w-full rounded-lg border border-gray-300 px-3.5 py-3 placeholder-gray-400 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p id="email-code-email-error" className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Above the button, not below the help panel: when Turnstile does
              show a checkbox it has to be where the person is already looking. */}
          <TurnstileWidget ref={turnstileRef} action="signin" onToken={rememberToken} />

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center min-h-[46px] py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Sending code...
              </span>
            ) : 'Send code'}
          </button>

          {/* Password sign-in remains supported but is intentionally hidden from this default flow. */}
          {onCreateAccount && (
            <div className="text-center text-sm text-gray-600">
              New to Serviti AI?{' '}
              {/* A real link: it can open in a new tab and be followed by crawlers. */}
              <a
                href="/auth/signup"
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                  event.preventDefault();
                  onCreateAccount(email.trim());
                }}
                className="font-semibold text-blue-600 hover:text-blue-500"
              >
                Sign up
              </a>
            </div>
          )}
        </form>
      )}

      {step === 'code' && (
        <form className="space-y-5" onSubmit={handleVerifyCode}>
          {info && (
            <p role="status" className="text-sm text-gray-500">{info}</p>
          )}

          <div>
            <label htmlFor="email-code-code" className="block text-sm font-medium text-gray-700">
              6-digit code
            </label>
            <input
              id="email-code-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="mt-2 appearance-none relative block w-full rounded-lg border border-gray-300 px-3.5 py-3 text-center text-lg tracking-[0.5em] text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="000000"
            />
          </div>

          {error && (
            <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <TurnstileWidget ref={turnstileRef} action="signin" onToken={rememberToken} />

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full flex justify-center items-center min-h-[46px] py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Verify and sign in'}
          </button>

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setError(''); }}
              className="min-h-[44px] text-left font-medium text-blue-600 hover:text-blue-500"
            >
              Use a different email
            </button>
            <button
              type="button"
              onClick={handleRequestCode}
              disabled={isLoading || cooldownLeft > 0}
              className="inline-flex min-h-[44px] items-center gap-1.5 font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
            >
              {isLoading && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />}
              {isLoading ? 'Sending...' : (cooldownLeft > 0 ? `Resend code in ${cooldownLeft}s` : 'Resend code')}
            </button>
          </div>
          {onCreateAccount && (
            <p className="text-center text-sm text-gray-600">
              New to Serviti?{' '}
              <a
                href={`/auth/signup?email=${encodeURIComponent(email.trim())}`}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
                  event.preventDefault();
                  onCreateAccount(email.trim());
                }}
                className="inline-flex min-h-[44px] items-center font-semibold text-blue-700 hover:underline"
              >
                Create an account
              </a>
            </p>
          )}
          <CodeHelp collapsible />
        </form>
      )}

      {step === 'password' && (
        <form className="space-y-5" onSubmit={handleVerifyPassword}>
          <div>
            <label htmlFor="email-code-password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="email-code-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 appearance-none relative block w-full rounded-lg border border-gray-300 px-3.5 py-3 placeholder-gray-400 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Enter password"
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !password}
            className="w-full flex justify-center items-center min-h-[46px] py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying...' : 'Verify and sign in'}
          </button>

          <div className="text-center text-sm">
            <button
              type="button"
              onClick={() => { setStep('email'); setCode(''); setPassword(''); setStepUpToken(''); setError(''); }}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Use a different email
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EmailCodeLoginForm;
